<template>
  <div v-if="show" class="mm-screen">

    <!-- Progress bar -->
    <div class="mm-progress-bar">
      <div class="mm-progress-fill" :style="{ width: progressPct + '%' }"></div>
      <span class="mm-progress-label">{{ correctCount }} / {{ blanks.length }}</span>
    </div>

    <!-- Word bank — visible mode only -->
    <div v-if="mode === 'visible'" class="mm-chip-bank">
      <template v-for="(chip, i) in chips">
        <div
          v-if="!chip.used"
          :key="'chip-' + i"
          :class="['mm-chip', { 'mm-chip-selected': selectedChip === i }]"
          @click="selectChip(i)"
        >{{ chip.word }}</div>
      </template>
      <span v-if="chips.every(c => c.used)" class="mm-bank-empty">✓</span>
    </div>

    <!-- Instruction hint -->
    <div class="mm-hint">
      <span v-if="mode === 'visible' && selectedChip === null">{{ translation.tapWord }}</span>
      <span v-else-if="mode === 'visible' && selectedChip !== null">{{ translation.tapBlank }}</span>
      <span v-else>{{ translation.typeWords }}</span>
    </div>

    <!-- Text with blanks -->
    <div class="mm-text-card">
      <div class="mm-letter-header">
        <div class="mm-letter-lines">
          <span></span><span></span><span></span>
        </div>
      </div>
      <div class="mm-text-content" :style="{ direction: textDir }">
        <template v-for="(seg, i) in segments">

          <!-- Plain text segment -->
          <span v-if="seg.type === 'text'" :key="'t' + i" class="mm-text-piece">{{ seg.value }}</span>

          <!-- Blank — visible mode -->
          <span v-else-if="mode === 'visible'" :key="'b' + i" class="mm-blank-wrap">
            <span
              :class="['mm-blank',
                { 'mm-blank-filled': blanks[seg.blankIndex].filled && !blanks[seg.blankIndex].correct,
                  'mm-blank-correct': blanks[seg.blankIndex].correct,
                  'mm-blank-active': selectedChip !== null && !blanks[seg.blankIndex].correct,
                  'mm-blank-wrong-flash': blanks[seg.blankIndex].wrongFlash }]"
              :style="{ minWidth: Math.max((blanks[seg.blankIndex].answer.length) * 12, 60) + 'px' }"
              @click="clickBlank(seg.blankIndex)"
            >
              <span v-if="blanks[seg.blankIndex].filled">{{ blanks[seg.blankIndex].filled }}</span>
              <span v-else class="mm-blank-placeholder">{{ '_'.repeat(Math.min(blanks[seg.blankIndex].answer.length, 6)) }}</span>
            </span>
          </span>

          <!-- Blank — hidden mode (type) -->
          <span v-else :key="'b' + i" class="mm-blank-wrap">
            <input
              v-model="blanks[seg.blankIndex].value"
              :class="['mm-input',
                { 'mm-input-correct': blanks[seg.blankIndex].correct,
                  'mm-input-wrong': blanks[seg.blankIndex].wrong }]"
              :disabled="blanks[seg.blankIndex].correct"
              :placeholder="'_'.repeat(Math.min(blanks[seg.blankIndex].answer.length, 8))"
              :style="{ width: Math.max(blanks[seg.blankIndex].answer.length * 1.2 + 2, 5) + 'ch' }"
              autocomplete="off"
              autocorrect="off"
              autocapitalize="off"
              spellcheck="false"
              @input="onInput(seg.blankIndex)"
              @blur="onBlur(seg.blankIndex)"
            />
          </span>

        </template>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  props: {
    pointData: { type: Object, default: null },
  },
  data() {
    return {
      correctAnswerSound: require("../../static/sounds/correctAnswer.mp3"),
      wrongAnswerSound:   require("../../static/sounds/wrongAnswer.mp3"),
      segments:    [],
      blanks:      [],
      chips:       [],
      selectedChip: null,
      translation: {
        tapWord:   'Appuyez sur un mot pour le sélectionner',
        tapBlank:  'Appuyez sur un espace pour y placer le mot',
        typeWords: 'Écrivez les mots manquants',
      },
    };
  },
  computed: {
    show() {
      return this.$parent.showMotManquant === true;
    },
    mode() {
      return this.pointData?.motManquantMode || 'visible';
    },
    correctCount() {
      return this.blanks.filter(b => b.correct).length;
    },
    progressPct() {
      if (!this.blanks.length) return 0;
      return (this.correctCount / this.blanks.length) * 100;
    },
    textDir() {
      const lang = this.$store.getters.lang;
      return (lang === 'he' || lang === 'ar') ? 'rtl' : 'ltr';
    },
  },
  watch: {
    // Re-parse whenever the source text changes (e.g. when pointData changes)
    'pointData.motManquantText': {
      immediate: true,
      handler(text) {
        this.parseAndInit(text || '');
      },
    },
  },
  mounted() {
    this.translate(this.$store.getters.lang);
  },
  methods: {
    // ── Parsing ──────────────────────────────────────────────
    parseAndInit(text) {
      const segments = [];
      const blanks   = [];
      const regex    = /\{([^}]+)\}/g;
      let lastIndex  = 0;
      let match;

      while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) {
          segments.push({ type: 'text', value: text.slice(lastIndex, match.index) });
        }
        segments.push({ type: 'blank', blankIndex: blanks.length });
        blanks.push({
          answer:     match[1],
          filled:     null,    // visible mode
          correct:    false,
          value:      '',      // hidden mode
          wrong:      false,
          wrongFlash: false,
        });
        lastIndex = match.index + match[0].length;
      }
      if (lastIndex < text.length) {
        segments.push({ type: 'text', value: text.slice(lastIndex) });
      }

      this.segments     = segments;
      this.blanks       = blanks;
      this.selectedChip = null;

      // Build shuffled word-bank chips (one per blank, even if duplicate word)
      const words = blanks.map(b => b.answer);
      const shuffled = [...words].sort(() => Math.random() - 0.5);
      this.chips = shuffled.map(word => ({ word, used: false }));
    },

    // ── Visible mode ─────────────────────────────────────────
    selectChip(i) {
      this.selectedChip = (this.selectedChip === i) ? null : i;
    },

    clickBlank(blankIndex) {
      const blank = this.blanks[blankIndex];
      if (blank.correct) return;

      if (this.selectedChip !== null) {
        const chipWord = this.chips[this.selectedChip].word;

        // Return previously placed word to bank before overwriting
        if (blank.filled) this.returnWordToBank(blankIndex);

        // Place chip
        blank.filled = chipWord;
        this.chips[this.selectedChip].used = true;
        this.selectedChip = null;

        this.checkVisibleBlank(blankIndex);
      } else if (blank.filled) {
        // No chip selected → tap on filled blank returns it
        this.returnWordToBank(blankIndex);
      }
    },

    returnWordToBank(blankIndex) {
      const blank = this.blanks[blankIndex];
      if (!blank.filled) return;
      const word = blank.filled;
      blank.filled  = null;
      blank.correct = false;
      // Mark the first matching used chip as available again
      const chip = this.chips.find(c => c.used && c.word === word);
      if (chip) chip.used = false;
    },

    checkVisibleBlank(blankIndex) {
      const blank     = this.blanks[blankIndex];
      const isCorrect = blank.filled?.toLowerCase().trim() === blank.answer.toLowerCase().trim();

      if (isCorrect) {
        blank.correct = true;
        this.playCorrect();
        this.checkAllDone();
      } else {
        // Wrong placement: flash red, bounce back after delay
        blank.wrongFlash = true;
        this.playWrong();
        setTimeout(() => {
          blank.wrongFlash = false;
          this.returnWordToBank(blankIndex);
        }, 700);
      }
    },

    // ── Hidden mode ───────────────────────────────────────────
    onInput(blankIndex) {
      const blank = this.blanks[blankIndex];
      if (blank.correct) return;
      if (blank.value.trim().toLowerCase() === blank.answer.trim().toLowerCase()) {
        blank.correct = true;
        blank.wrong   = false;
        this.playCorrect();
        this.checkAllDone();
      }
    },

    onBlur(blankIndex) {
      const blank = this.blanks[blankIndex];
      if (blank.correct || !blank.value.trim()) return;
      // Non-empty wrong answer on blur → penalise
      blank.wrong = true;
      this.playWrong();
      setTimeout(() => { blank.wrong = false; }, 1000);
    },

    // ── Shared ────────────────────────────────────────────────
    playCorrect() {
      new Audio(this.correctAnswerSound).play().catch(() => {});
      this.$parent.errorNumber++;
      this.$parent.showStarUp('1+');
      localStorage.setItem('errorNumber', this.$parent.errorNumber);
    },

    playWrong() {
      new Audio(this.wrongAnswerSound).play().catch(() => {});
      this.$parent.errorNumber--;
      localStorage.setItem('errorNumber', this.$parent.errorNumber);
      if (this.$parent.$refs.pointsCounting) {
        this.$parent.$refs.pointsCounting.classList.add('redColor');
      }
      this.$parent.showWongAnswerIcon = true;
      setTimeout(() => {
        if (this.$parent.$refs.pointsCounting) {
          this.$parent.$refs.pointsCounting.classList.remove('redColor');
        }
        this.$parent.showWongAnswerIcon = false;
      }, 1000);
    },

    checkAllDone() {
      if (this.blanks.every(b => b.correct)) {
        setTimeout(() => { this.$emit('mm-done'); }, 700);
      }
    },

    translate(lang) {
      const map = {
        fr: {
          tapWord:   'Appuyez sur un mot pour le sélectionner',
          tapBlank:  'Appuyez sur un espace pour y placer le mot',
          typeWords: 'Écrivez les mots manquants dans les espaces',
        },
        he: {
          tapWord:   'לחצו על מילה לבחירה',
          tapBlank:  'לחצו על הריק להכנסת המילה',
          typeWords: 'כתבו את המילים החסרות',
        },
        en: {
          tapWord:   'Tap a word to select it',
          tapBlank:  'Tap a blank to place the word',
          typeWords: 'Type the missing words',
        },
        de: {
          tapWord:   'Tippen Sie auf ein Wort, um es auszuwählen',
          tapBlank:  'Tippen Sie auf eine Lücke',
          typeWords: 'Schreiben Sie die fehlenden Wörter',
        },
        ar: {
          tapWord:   'انقر على كلمة لتحديدها',
          tapBlank:  'انقر على الفراغ لوضع الكلمة',
          typeWords: 'اكتب الكلمات المفقودة في الفراغات',
        },
      };
      this.translation = map[lang] || map.en;
    },
  },
};
</script>

<style scoped>
/* ── Root ─────────────────────────────────────────────── */
.mm-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 12px 80px;
  min-height: 60vh;
  gap: 16px;
  width: 100%;
}

/* ── Progress bar ─────────────────────────────────────── */
.mm-progress-bar {
  position: relative;
  width: 100%;
  max-width: 560px;
  height: 22px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 11px;
  overflow: hidden;
}

.mm-progress-fill {
  height: 100%;
  background: #4caf50;
  border-radius: 11px;
  transition: width 0.4s ease;
}

.mm-progress-label {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'VarelaRound', sans-serif;
  font-size: 12px;
  font-weight: bold;
  color: white;
}

/* ── Hint ─────────────────────────────────────────────── */
.mm-hint {
  font-family: 'VarelaRound', sans-serif;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
  min-height: 20px;
}

/* ── Word bank ────────────────────────────────────────── */
.mm-chip-bank {
  width: 100%;
  max-width: 560px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  min-height: 52px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 18px;
}

.mm-chip {
  font-family: 'VarelaRound', sans-serif;
  font-size: 16px;
  font-weight: bold;
  background: white;
  color: #1a5fa8;
  border-radius: 50px;
  padding: 8px 20px;
  cursor: pointer;
  box-shadow: 0 4px 0 #b0bec5, 0 6px 10px rgba(0, 0, 0, 0.25);
  transition: transform 80ms, box-shadow 80ms;
  user-select: none;
}

.mm-chip:active {
  transform: translateY(3px);
  box-shadow: 0 1px 0 #b0bec5;
}

.mm-chip-selected {
  background: #1976d2;
  color: white;
  box-shadow: 0 4px 0 #0d47a1, 0 6px 14px rgba(0, 0, 0, 0.35);
  outline: 3px solid #90caf9;
}

.mm-bank-empty {
  font-size: 20px;
  opacity: 0.5;
}

/* ── Text card — letter style ─────────────────────────── */
.mm-text-card {
  width: 100%;
  max-width: 560px;
  background: #fdf6e3;
  background-image:
    repeating-linear-gradient(
      transparent,
      transparent 39px,
      #c9b99a 39px,
      #c9b99a 40px
    );
  border: 1px solid #c9a96e;
  border-radius: 6px;
  padding: 20px 28px 32px;
  box-shadow:
    0 1px 3px rgba(0,0,0,0.18),
    0 6px 20px rgba(0,0,0,0.22),
    inset 0 1px 0 rgba(255,255,255,0.9);
  position: relative;
}

/* Decorative left margin line */
.mm-text-card::before {
  content: '';
  position: absolute;
  left: 52px;
  top: 0;
  bottom: 0;
  width: 1px;
  background: rgba(200, 100, 100, 0.35);
  border-radius: 1px;
}

/* Letter header with short decorative dashes */
.mm-letter-header {
  margin-bottom: 12px;
  padding-bottom: 8px;
}

.mm-letter-lines {
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding-left: 44px;
}

.mm-letter-lines span {
  display: block;
  height: 2px;
  background: #c9a96e;
  border-radius: 2px;
  opacity: 0.5;
}
.mm-letter-lines span:nth-child(1) { width: 45%; }
.mm-letter-lines span:nth-child(2) { width: 30%; }
.mm-letter-lines span:nth-child(3) { width: 55%; }

.mm-text-content {
  font-family: 'DorianBook', Georgia, 'Times New Roman', serif;
  font-size: 21px;
  color: #2d1a0a;
  line-height: 2.5;
  text-align: center;
  padding-left: 20px;
}

.mm-text-piece {
  white-space: pre-wrap;
}

.mm-blank-wrap {
  display: inline-block;
  vertical-align: middle;
  margin: 0 3px;
}

/* ── Visible mode blank ───────────────────────────────── */
.mm-blank {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 60px;
  padding: 2px 12px;
  border-bottom: 2px solid #8b6914;
  border-radius: 4px;
  cursor: pointer;
  font-family: 'DorianBook', Georgia, serif;
  font-size: 20px;
  color: #2d1a0a;
  transition: background 0.2s, border-color 0.2s, box-shadow 0.2s;
  white-space: nowrap;
}

.mm-blank-placeholder {
  opacity: 0.4;
  letter-spacing: 4px;
  font-size: 13px;
  font-family: monospace;
}

.mm-blank-filled {
  background: rgba(139, 105, 20, 0.1);
  border-color: #5a3e10;
}

.mm-blank-correct {
  background: rgba(56, 142, 60, 0.15) !important;
  border-color: #2e7d32 !important;
  color: #1b5e20;
  cursor: default;
}

.mm-blank-active {
  border-color: #1565c0;
  background: rgba(21, 101, 192, 0.08);
  box-shadow: 0 0 0 2px rgba(21, 101, 192, 0.2);
}

.mm-blank-wrong-flash {
  background: rgba(183, 28, 28, 0.12) !important;
  border-color: #b71c1c !important;
  animation: mm-shake 0.35s ease;
}

/* ── Hidden mode input ────────────────────────────────── */
.mm-input {
  font-family: 'DorianBook', Georgia, serif;
  font-size: 20px;
  color: #2d1a0a;
  background: transparent;
  border: none;
  border-bottom: 2px solid #8b6914;
  border-radius: 0;
  padding: 2px 6px;
  text-align: center;
  outline: none;
  transition: border-color 0.2s, background 0.2s;
  vertical-align: middle;
}

.mm-input::placeholder {
  color: rgba(45, 26, 10, 0.25);
  letter-spacing: 3px;
  font-size: 12px;
  font-family: monospace;
}

.mm-input:focus {
  border-color: #1565c0;
  background: rgba(21, 101, 192, 0.06);
}

.mm-input-correct {
  border-color: #2e7d32 !important;
  background: rgba(56, 142, 60, 0.1) !important;
  color: #1b5e20 !important;
}

.mm-input-wrong {
  border-color: #b71c1c !important;
  background: rgba(183, 28, 28, 0.08) !important;
  animation: mm-shake 0.35s ease;
}

/* ── Animations ───────────────────────────────────────── */
@keyframes mm-shake {
  0%, 100% { transform: translateX(0); }
  20%       { transform: translateX(-7px); }
  60%       { transform: translateX(7px); }
  80%       { transform: translateX(-3px); }
}
</style>
